export type ModeId = "palace" | "link" | "recall" | "names" | "numbers" | "spaced";

export type LevelState = {
  level: number;
  strongRuns: number;
  weakRuns: number;
  sessions: number;
  masteryCycles: number;
};

export type Difficulty = {
  itemCount: number;
  digitLength: number;
  studyMs: number;
  batchSize: number;
  minEvidence: number;
};

export const freshLevel = (): LevelState => ({
  level: 1,
  strongRuns: 0,
  weakRuns: 0,
  sessions: 0,
  masteryCycles: 0,
});

function safeInteger(value: unknown, fallback: number, minimum: number, maximum: number) {
  const parsed = typeof value === "number" && Number.isFinite(value) ? Math.floor(value) : fallback;
  return Math.max(minimum, Math.min(maximum, parsed));
}

export function normalizeLevelState(value: unknown): LevelState {
  if (!value || typeof value !== "object") return freshLevel();
  const source = value as Partial<LevelState>;
  return {
    level: safeInteger(source.level, 1, 1, 10),
    strongRuns: safeInteger(source.strongRuns, 0, 0, 1),
    weakRuns: safeInteger(source.weakRuns, 0, 0, 1),
    sessions: safeInteger(source.sessions, 0, 0, 1_000_000),
    masteryCycles: safeInteger(source.masteryCycles, 0, 0, 1_000_000),
  };
}

export function normalizeLevels(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {} as Record<string, LevelState>;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([mode, state]) => [
      mode,
      normalizeLevelState(state),
    ]),
  );
}

const COUNTS: Record<ModeId, number[]> = {
  palace: [4, 5, 5, 6, 6, 7, 7, 8, 9, 9],
  link: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
  recall: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
  names: [4, 4, 5, 5, 6, 6, 7, 8, 9, 10],
  numbers: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  spaced: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

const DIGIT_LENGTHS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

export function difficultyFor(mode: ModeId, level: number): Difficulty {
  const normalizedLevel = Math.max(1, Math.min(10, Math.floor(level)));
  const index = normalizedLevel - 1;
  const itemCount = COUNTS[mode][index];
  const studyMs = mode === "names"
    ? itemCount * Math.max(1_400, 2_200 - normalizedLevel * 70)
    : mode === "palace"
      ? itemCount * Math.max(1_500, 2_400 - normalizedLevel * 70)
      : mode === "recall"
        ? itemCount * Math.max(650, 1_150 - normalizedLevel * 40)
        : mode === "spaced"
          ? 0
          : 0;
  return {
    itemCount,
    digitLength: mode === "numbers" ? DIGIT_LENGTHS[index] : 0,
    studyMs,
    batchSize: mode === "spaced" ? itemCount : itemCount,
    minEvidence: mode === "spaced" ? 3 : Math.max(1, itemCount),
  };
}

export function advanceLevel({
  state,
  ratio,
  retentionCheck,
  attempts,
  minEvidence = 1,
}: {
  state: LevelState;
  ratio: number;
  retentionCheck: boolean;
  attempts: number;
  minEvidence?: number;
}) {
  const previous = normalizeLevelState(state);
  let nextLevel = previous.level;
  let strongRuns = previous.strongRuns;
  let weakRuns = previous.weakRuns;
  let masteryCycles = previous.masteryCycles;
  let masteryGained = false;

  if (attempts >= minEvidence) {
    if (retentionCheck) {
      weakRuns = 0;
      if (ratio < 0.6) {
        nextLevel = Math.max(1, previous.level - 1);
        strongRuns = 0;
      }
    } else {
      strongRuns = ratio >= 0.8 ? previous.strongRuns + 1 : 0;
      weakRuns = ratio < 0.5 ? previous.weakRuns + 1 : 0;
      if (strongRuns >= 2) {
        if (previous.level < 10) nextLevel = previous.level + 1;
        else {
          masteryCycles += 1;
          masteryGained = true;
        }
        strongRuns = 0;
      } else if (weakRuns >= 2) {
        nextLevel = Math.max(1, previous.level - 1);
        weakRuns = 0;
      }
    }
  }

  const nextState: LevelState = {
    level: nextLevel,
    strongRuns,
    weakRuns,
    sessions: previous.sessions + 1,
    masteryCycles,
  };
  return {
    state: nextState,
    levelChange: nextLevel - previous.level,
    masteryGained,
  };
}
